/**
 * Where the browser keeps the token pair — and the honest cost of that choice.
 *
 * ── The decision ──────────────────────────────────────────────────────────
 * ONE key in `localStorage` (`aim.session.v1`) holding the whole session blob.
 *
 * ── Why not the alternatives ──────────────────────────────────────────────
 * - **`httpOnly` cookie** is the genuinely secure option and is what the
 *   outgoing Auth.js session uses. It is off the table here because the SPA is
 *   a static bundle talking to a token API: the browser cannot read an
 *   `httpOnly` cookie, so it cannot attach `Authorization: Bearer`, cannot see
 *   when the access token is near expiry, and cannot drive the single-flight
 *   rotation the FastAPI backend requires. Making it work needs FastAPI to set
 *   a refresh cookie and expose a cookie-authenticated `/refresh` — real
 *   backend work, recorded below as the hardening path.
 * - **Memory only** is the safest storage there is and it fails FE-04: F5
 *   would sign the user out.
 * - **`sessionStorage`** dies when the tab closes. That is a smaller window of
 *   persistence, but NOT a smaller window of exposure: script running on the
 *   origin reads `sessionStorage` exactly as easily as `localStorage`. What it
 *   would buy is a behaviour change users would notice immediately — a new tab
 *   would demand a fresh login, where today's cookie session follows them
 *   across tabs and browser restarts. The milestone's acceptance test is that
 *   nothing changes for the 8 family members, so `localStorage` it is. The
 *   choice is a one-line config flip (`VITE_TOKEN_STORAGE=session`) if that
 *   judgement is ever revisited.
 *
 * ── The trade-off, stated plainly ─────────────────────────────────────────
 * This is strictly weaker than the `httpOnly` cookie it replaces. Today, an
 * XSS bug lets an attacker act as the user *while the page is open*. After
 * this change, the same bug lets them read a 60-day refresh token out of
 * `localStorage` and keep acting as the user from their own machine, long
 * after the victim closes the tab. There is no way to dress that up: web
 * storage is readable by any JavaScript on the origin, and choosing
 * `sessionStorage` would not fix it either.
 *
 * What actually reduces the damage, and is either already true or is a
 * follow-up someone must own:
 *   1. Access tokens live 15 minutes (server-side, already true).
 *   2. Refresh tokens ROTATE on every use and reuse revokes the whole device
 *      family — a stolen token that the victim then uses is detected and kills
 *      both sessions (`services/mobile_auth.py`, already true).
 *   3. Sign-out calls `/revoke` server-side, so a cleared browser is a dead
 *      token, not just a forgotten one (implemented in `session.ts`).
 *   4. The SPA contains no `dangerouslySetInnerHTML` and no `eval` — the XSS
 *      has to come from a dependency or from nginx-served content.
 *   5. **Not yet done, and the real fix:** a strict `Content-Security-Policy`
 *      at the nginx layer, and eventually an `httpOnly` refresh cookie issued
 *      by FastAPI. Both are outside `web/**` and are called out in the S1
 *      hand-off rather than quietly assumed.
 */

export interface TokenStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/** Test double, and the fallback when the browser refuses persistent storage. */
export function createMemoryStorage(seed: Record<string, string> = {}): TokenStorage {
  const map = new Map<string, string>(Object.entries(seed));
  return {
    async getItem(key) {
      return map.has(key) ? (map.get(key) as string) : null;
    },
    async setItem(key, value) {
      map.set(key, value);
    },
    async removeItem(key) {
      map.delete(key);
    },
  };
}

/**
 * Wrap a synchronous `Storage` in the async interface.
 *
 * `setItem` is allowed to THROW (quota, or Safari private mode, where
 * `localStorage` exists but every write raises). `session.ts` treats a failed
 * write as fatal and clears the session, because the server has already
 * rotated the token by then — see the `persist` note there.
 */
export function fromWebStorage(store: Storage): TokenStorage {
  return {
    async getItem(key) {
      return store.getItem(key);
    },
    async setItem(key, value) {
      store.setItem(key, value);
    },
    async removeItem(key) {
      store.removeItem(key);
    },
  };
}

export type TokenStorageKind = "local" | "session" | "memory";

/** `VITE_TOKEN_STORAGE` — `local` (default), `session`, or `memory`. */
export function configuredStorageKind(): TokenStorageKind {
  const raw = import.meta.env?.VITE_TOKEN_STORAGE;
  return raw === "session" || raw === "memory" ? raw : "local";
}

/**
 * Probe a `Storage` for real usability. Merely existing is not enough: in
 * Safari private mode `window.localStorage` is present and every `setItem`
 * throws, so a session written there would be silently lost.
 */
function usable(store: Storage | undefined | null): store is Storage {
  if (!store) return false;
  try {
    const probe = "__aim_probe__";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * The storage the app actually uses. Falls back to memory when the browser
 * denies persistence — the user stays signed in for the life of the page
 * instead of being unable to sign in at all.
 */
export function createBrowserTokenStorage(
  kind: TokenStorageKind = configuredStorageKind(),
): TokenStorage {
  if (kind === "memory" || typeof window === "undefined") return createMemoryStorage();
  const store = kind === "session" ? window.sessionStorage : window.localStorage;
  return usable(store) ? fromWebStorage(store) : createMemoryStorage();
}
